import { useCallback, useEffect, useRef } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { useLiveField, useLiveKey, LiveDrafts } from '../live/LiveField';

// A one-line text field (a title, a to-do label) that wraps and grows instead
// of scrolling sideways, so a long text stays readable while it's typed. It
// never holds a line break: Enter is left to the caller (validate), and a
// pasted line break becomes a space.
export function LineTextarea({ value, onChange, onKeyDown, onBlur, placeholder, autoFocus, className, style, liveKey, liveField }: {
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Live: the others see what is typed here (live/LiveField.tsx). */
  liveKey?: string | null;
  liveField?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const live = useLiveField(useLiveKey(liveKey, liveField), value, undefined, onBlur);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, []);
  useEffect(() => { resize(); }, [value, resize]);

  return (
    <>
      <textarea
        ref={ref}
        rows={1}
        className={`line-textarea ${className ?? 'form-input--bare'}`}
        style={style}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value.replace(/\s*[\r\n]+\s*/g, ' '))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
          onKeyDown?.(e);
        }}
        onFocus={live.onFocus}
        onBlur={live.onBlur}
      />
      <LiveDrafts drafts={live.drafts} />
    </>
  );
}
